import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BackendResponse } from '@fc-core/models/response.model';
import { ChatMessage, RawChatMessage } from '../models/chat-message';
import { BotWidgetOutput } from '../models/bot-widget';
import { lastValueFrom, map } from 'rxjs';
import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class ChatMessagesApiService {
  private http = inject(HttpClient);

  getInitialMessages(
    channelId: string,
    limit: number,
  ): Promise<BackendResponse<ChatMessage[]>> {
    return lastValueFrom(
      this.http
        .get<
          BackendResponse<ChatMessage[]>
        >(`${environment.apiUrl + 'api/chat/channel/'}${channelId}/messages/?limit=${limit}`)
        .pipe(
          map((response) => {
            const sortedMessages = response.results.sort(
              (a, b) =>
                new Date(a.meta?.created ?? 0).getTime() -
                new Date(b.meta?.created ?? 0).getTime(),
            );
            return {
              ...response,
              results: sortedMessages,
            };
          }),
        ),
    );
  }

  getPageMessages(pageUrl: string): Promise<BackendResponse<ChatMessage[]>> {
    return lastValueFrom(
      this.http.get<BackendResponse<ChatMessage[]>>(pageUrl),
    );
  }

  getMessageByCursor(
    message: ChatMessage,
    limit: number,
    reverse: boolean = false,
  ): any {
    // CURSOR FORMAT:
    // p - position (timestamp) ascii (including microseconds) iso8601 example: 2025-05-22+11:40:59.210898+00:00
    // r - reverse (1/0)

    const formattedTimestamp = moment(message.meta?.created)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss.SSSSSS[+00:00]');

    let cursorValue = '';
    if (reverse) {
      cursorValue = 'r=1&p=' + encodeURIComponent(formattedTimestamp);
    } else {
      cursorValue = 'p=' + encodeURIComponent(formattedTimestamp);
    }
    const cursor = btoa(cursorValue);
    const urlObj = new URL(
      `${environment.apiUrl}api/chat/channel/${message.channel}/messages/`,
    );
    urlObj.searchParams.set('cursor', cursor);
    urlObj.searchParams.set('limit', limit.toString());

    return lastValueFrom(
      this.http.get<BackendResponse<ChatMessage[]>>(urlObj.toString()).pipe(
        map((response) => {
          let messages: ChatMessage[];
          if (reverse) {
            messages = response.results.slice(0, -1);
          } else {
            messages = response.results;
          }
          return {
            ...response,
            results: messages.reverse(),
          };
        }),
      ),
    );
  }

  getMessages(
    channelId: string,
    offset: number,
    limit: number,
  ): Promise<BackendResponse<ChatMessage[]>> {
    return lastValueFrom(
      this.http.get<BackendResponse<ChatMessage[]>>(
        `${environment.apiUrl + 'api/chat/channel/'}${channelId}/messages/?limit=${limit}&offset=${offset}`,
      ),
    );
  }

  patchMessage(message: Partial<RawChatMessage>): Promise<ChatMessage> {
    return lastValueFrom(
      this.http.patch<ChatMessage>(
        `${environment.apiUrl}api/chat/messages/${message.id}/`,
        message,
      ),
    );
  }

  deleteMessage(messageId: string): Promise<void> {
    return lastValueFrom(
      this.http.delete<void>(
        `${environment.apiUrl}api/chat/messages/${messageId}/`,
      ),
    );
  }

  sendReaction(messageId: string, emoji: string): Promise<ChatMessage> {
    return lastValueFrom(
      this.http.patch<ChatMessage>(
        `${environment.apiUrl}api/chat/messages/${messageId}/reactions/`,
        {
          emoji,
        },
      ),
    );
  }

  execMessageActions(messageId: string, actionId: string): Promise<void> {
    return lastValueFrom(
      this.http.post<void>(
        `${environment.apiUrl}api/chat/messages/${messageId}/actions/${actionId}/`,
        {},
      ),
    );
  }

  deleteMessageAction(messageId: string, actionId: string): Promise<void> {
    return lastValueFrom(
      this.http.delete<void>(
        `${environment.apiUrl}api/chat/messages/${messageId}/actions/${actionId}/`,
      ),
    );
  }

  dismissWidget(interactionId: string): Promise<void> {
    return lastValueFrom(
      this.http.delete<void>(
        `${environment.apiUrl}api/chat/interactions/${interactionId}/`,
      ),
    );
  }

  submitWidgetOutput(
    interactionId: string,
    output: BotWidgetOutput,
  ): Promise<ChatMessage | null> {
    return lastValueFrom(
      this.http.post<ChatMessage | null>(
        `${environment.apiUrl}api/chat/interactions/${interactionId}/`,
        output,
      ),
    );
  }
}
